/**
 * App — Admin dashboard root component.
 * Lazy-loaded routes with admin auth protection via AdminLayout.
 */

import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { ADMIN_ROUTES } from '@wonderherbals/shared';
import AdminLayout from './components/AdminLayout';

// ============================================================
// Lazy-loaded pages
// ============================================================

const AdminLogin = lazy(() => import('./pages/AdminLogin'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const ProductList = lazy(() => import('./pages/ProductList'));
const ProductForm = lazy(() => import('./pages/ProductForm'));
const IngredientList = lazy(() => import('./pages/IngredientList'));
const IngredientForm = lazy(() => import('./pages/IngredientForm'));
const ConcernList = lazy(() => import('./pages/ConcernList'));
const ConcernForm = lazy(() => import('./pages/ConcernForm'));
const CollectionList = lazy(() => import('./pages/CollectionList'));
const CollectionForm = lazy(() => import('./pages/CollectionForm'));
const OrderList = lazy(() => import('./pages/OrderList'));
const OrderDetail = lazy(() => import('./pages/OrderDetail'));
const CustomerList = lazy(() => import('./pages/CustomerList'));
const CustomerDetail = lazy(() => import('./pages/CustomerDetail'));
const HomepageBuilder = lazy(() => import('./pages/HomepageBuilder'));
const PageBuilder = lazy(() => import('./pages/PageBuilder'));
const BlogList = lazy(() => import('./pages/BlogList'));
const BlogForm = lazy(() => import('./pages/BlogForm'));
const CouponList = lazy(() => import('./pages/CouponList'));
const Analytics = lazy(() => import('./pages/Analytics'));
const Inbox = lazy(() => import('./pages/Inbox'));
const Settings = lazy(() => import('./pages/Settings'));
const ImportShopify = lazy(() => import('./pages/ImportShopify'));
const ReviewList = lazy(() => import('./pages/ReviewList'));
const TaskList = lazy(() => import('./pages/TaskList'));
const MetaHub = lazy(() => import('./pages/MetaHub'));
const InstagramStudio = lazy(() => import('./pages/InstagramStudio'));
const WhatsAppHub = lazy(() => import('./pages/WhatsAppHub'));
const SEODashboard = lazy(() => import('./pages/SEODashboard'));
const LogisticsDashboard = lazy(() => import('./pages/LogisticsDashboard'));
const StaffHub = lazy(() => import('./pages/StaffHub'));
const WhatsAppBrain = lazy(() => import('./pages/WhatsAppBrain'));
const AppIntelligence = lazy(() => import('./pages/AppIntelligence'));

const MarketingAutomation = lazy(() => import('./pages/MarketingAutomation'));

// ============================================================
// Page Loader
// ============================================================

function PageLoader() {
    return (
        <div className="flex items-center justify-center min-h-[400px]">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-admin-forest" />
        </div>
    );
}

// ============================================================
// App Component
// ============================================================

export default function App() {
    return (
        <Suspense fallback={<PageLoader />}>
            <Routes>
                {/* Login — outside AdminLayout */}
                <Route path={ADMIN_ROUTES.LOGIN} element={<AdminLogin />} />

                {/* All admin routes — wrapped in AdminLayout (auth guard + sidebar + header) */}
                <Route element={<AdminLayout />}>
                    <Route path={ADMIN_ROUTES.DASHBOARD} element={<Dashboard />} />

                    {/* Products */}
                    <Route path={ADMIN_ROUTES.PRODUCTS} element={<ProductList />} />
                    <Route path={ADMIN_ROUTES.PRODUCT_NEW} element={<ProductForm />} />
                    <Route path={ADMIN_ROUTES.PRODUCT_EDIT} element={<ProductForm />} />

                    {/* Ingredients */}
                    <Route path={ADMIN_ROUTES.INGREDIENTS} element={<IngredientList />} />
                    <Route path={ADMIN_ROUTES.INGREDIENT_NEW} element={<IngredientForm />} />
                    <Route path={ADMIN_ROUTES.INGREDIENT_EDIT} element={<IngredientForm />} />

                    {/* Concerns */}
                    <Route path={ADMIN_ROUTES.CONCERNS} element={<ConcernList />} />
                    <Route path={ADMIN_ROUTES.CONCERN_NEW} element={<ConcernForm />} />
                    <Route path={ADMIN_ROUTES.CONCERN_EDIT} element={<ConcernForm />} />

                    {/* Collections */}
                    <Route path={ADMIN_ROUTES.COLLECTIONS} element={<CollectionList />} />
                    <Route path={ADMIN_ROUTES.COLLECTION_NEW} element={<CollectionForm />} />
                    <Route path={ADMIN_ROUTES.COLLECTION_EDIT} element={<CollectionForm />} />

                    {/* Orders */}
                    <Route path={ADMIN_ROUTES.ORDERS} element={<OrderList />} />
                    <Route path={ADMIN_ROUTES.ORDER_DETAIL} element={<OrderDetail />} />

                    {/* Customers */}
                    <Route path={ADMIN_ROUTES.CUSTOMERS} element={<CustomerList />} />
                    <Route path={ADMIN_ROUTES.CUSTOMER_DETAIL} element={<CustomerDetail />} />

                    {/* Homepage Builder */}
                    <Route path={ADMIN_ROUTES.HOMEPAGE} element={<HomepageBuilder />} />

                    {/* Pages */}
                    <Route path={ADMIN_ROUTES.PAGES} element={<PageBuilder />} />
                    <Route path={ADMIN_ROUTES.PAGE_EDIT} element={<PageBuilder />} />

                    {/* Blog */}
                    <Route path={ADMIN_ROUTES.BLOGS} element={<BlogList />} />
                    <Route path={ADMIN_ROUTES.BLOG_NEW} element={<BlogForm />} />
                    <Route path={ADMIN_ROUTES.BLOG_EDIT} element={<BlogForm />} />

                    {/* Marketing */}
                    <Route path={ADMIN_ROUTES.COUPONS} element={<CouponList />} />
                    <Route path={ADMIN_ROUTES.FOLLOWUPS} element={<MarketingAutomation />} />

                    {/* Reviews */}
                    <Route path={ADMIN_ROUTES.REVIEWS} element={<ReviewList />} />

                    {/* Chat */}
                    <Route path={ADMIN_ROUTES.CHAT} element={<Inbox />} />

                    {/* Analytics */}
                    <Route path={ADMIN_ROUTES.ANALYTICS} element={<Analytics />} />

                    {/* Settings */}
                    <Route path={ADMIN_ROUTES.SETTINGS} element={<Settings />} />

                    {/* Tasks */}
                    <Route path={ADMIN_ROUTES.TASKS} element={<TaskList />} />

                    {/* Meta Hub */}
                    <Route path={ADMIN_ROUTES.META_HUB} element={<MetaHub />} />

                    {/* Instagram */}
                    <Route path={ADMIN_ROUTES.INSTAGRAM} element={<InstagramStudio />} />

                    {/* WhatsApp */}
                    <Route path={ADMIN_ROUTES.WHATSAPP_HUB} element={<WhatsAppHub />} />
                    <Route path={ADMIN_ROUTES.WHATSAPP_BRAIN} element={<WhatsAppBrain />} />

                    {/* SEO */}
                    <Route path={ADMIN_ROUTES.SEO_DASHBOARD} element={<SEODashboard />} />

                    {/* Logistics */}
                    <Route path={ADMIN_ROUTES.LOGISTICS} element={<LogisticsDashboard />} />

                    {/* Staff */}
                    <Route path={ADMIN_ROUTES.STAFF} element={<StaffHub />} />

                    {/* Mobile App */}
                    <Route path={ADMIN_ROUTES.MOBILE_APP} element={<AppIntelligence />} />

                    {/* Import */}
                    <Route path={ADMIN_ROUTES.IMPORT_SHOPIFY} element={<ImportShopify />} />
                </Route>

                {/* Catch-all redirect */}
                <Route path="*" element={<Navigate to={ADMIN_ROUTES.DASHBOARD} replace />} />
            </Routes>
        </Suspense>
    );
}
